import React, { useEffect, useState, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";

import SliderTooltip from "./SliderTooltip";
import { videoPlayerService } from "../../../../../services/video-player/VideoPlayerService";
import { VideoAnalysisService } from "../../../../../services/inspection/VideoAnalysisService";
import {
  GroupedSegmentations,
  Segmentation,
} from "../../../../../api-models/response/VideoAnalysisDataResponse";

interface VideoSliderProps {
  videoDuration: number;
  currentTime: number;
  bufferedTime?: number;
  onChange: (time: number) => void;
  videoAnalysis?: VideoAnalysisService;
  filteredSegmentations?: Segmentation[];
}

export default observer(function VideoSlider(props: VideoSliderProps) {
  const {
    videoDuration = 100,
    currentTime = 0,
    bufferedTime = 0,
    onChange,
    videoAnalysis,
    filteredSegmentations,
  } = props;
  const { player } = videoPlayerService;

  const [isSeeking, setIsSeeking] = useState(false);
  const [trackElement, setTrackElement] = useState<HTMLDivElement | null>(null);

  const [trackWidth, setTrackWidth] = useState(0);
  const [seekHoverPosition, setSeekHoverPosition] = useState(0);
  const [segmentationGroupDetail, setSegmentationGroupDetail] = useState<
    GroupedSegmentations[] | undefined
  >();

  const CLOSEST_PREDICTION_THRESHOLD = 1;
  const detectedGroupCodes = JSON.stringify(videoAnalysis?.detectedGroupCodes);

  const segmentationGroups = useMemo(() => {
    if (!videoAnalysis?.segmentationIsAvailable) return null;

    return filteredSegmentations
      ?.filter((s) => {
        var detectedGroup = videoAnalysis?.detectedGroupCodes.find((g) => g.code === s.groupCode);
        return detectedGroup?.isVisible && detectedGroup?.isDetected;
      })
      ?.filter((s) => s.timestamp <= videoDuration)
      ?.sort((a, b) => a.timestamp - b.timestamp)
      ?.reduce<Segmentation[][]>((out, s, i, array) => {
        if (i === 0 || s.timestamp - array[i - 1].timestamp > CLOSEST_PREDICTION_THRESHOLD)
          out.push([s]);
        else out[out.length - 1].push(s);
        return out;
      }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    videoDuration,
    detectedGroupCodes,
    videoAnalysis?.analysisIsAvailable,
    videoAnalysis?.segmentationIsAvailable,
    videoAnalysis?.highlightDetectionConfidenceOption,
    videoAnalysis?.highlightDetectionOverallConfidence,
    videoAnalysis?.detectionIndividualConfidenceLevels,
  ]);

  const currentSegmentationGroupDetail = useMemo(() => {
    const percent: number = (seekHoverPosition * 100) / trackWidth;
    const seconds: number = Math.round(+(percent * (videoDuration / 100)));

    return segmentationGroups
      ?.find((g) => {
        const startTime = Math.floor(g[0].timestamp);
        const endTime = Math.floor(g[g.length - 1].timestamp);
        const endTimeApproximate = Math.floor(g[g.length - 1].timestamp + 1);
        return (
          (startTime < seconds && seconds < endTimeApproximate) ||
          (startTime === endTime && startTime === seconds)
        );
      })
      ?.reduce<GroupedSegmentations[]>((out, s) => {
        const group = out.find((x) => x.groupCode === s.groupCode);
        if (group) group.segmentations.push(s);
        else out.push({ groupCode: s.groupCode, segmentations: [s] });
        return out;
      }, []);
  }, [seekHoverPosition, segmentationGroups, trackWidth, videoDuration]);

  const allSegmentationsGroupDetail = useMemo(() => {
    let finalGroupedArray: GroupedSegmentations[] = [];

    segmentationGroups?.forEach((group) => {
      const groupedSegmentations = group.reduce((result, segmentation) => {
        const { groupCode } = segmentation;
        const existingGroup = result.find((group) => group.groupCode === groupCode);

        if (existingGroup) {
          existingGroup.segmentations.push(segmentation);
        } else {
          result.push({
            groupCode,
            segmentations: [segmentation],
          });
        }

        return result;
      }, [] as GroupedSegmentations[]);

      finalGroupedArray = finalGroupedArray.concat(groupedSegmentations);
    });

    return finalGroupedArray;
  }, [segmentationGroups]);

  useEffect(() => {
    if (videoAnalysis?.showAllSegmentationBars) {
      setSegmentationGroupDetail(allSegmentationsGroupDetail);
    } else {
      setSegmentationGroupDetail(currentSegmentationGroupDetail);
    }
  }, [allSegmentationsGroupDetail, currentSegmentationGroupDetail, videoAnalysis?.showAllSegmentationBars]);

  const getMousePosition = useCallback(
    (mouseHorizontalCoordinate: number) =>
      mouseHorizontalCoordinate - (trackElement?.getBoundingClientRect().left ?? 0),
    [trackElement]
  );

  const changeCurrentTimePosition = useCallback(
    (pageX: number): void => {
      let position: number = getMousePosition(pageX);
      position = position < 0 ? 0 : position;
      position = position > trackWidth ? trackWidth : position;
      setSeekHoverPosition(position);

      let percent: number = (position * 100) / trackWidth;
      let time: number = +(percent * (videoDuration / 100));
      onChange(time);
    },
    [getMousePosition, trackWidth, videoDuration, onChange]
  );

  const handleSeeking = useCallback(
    (event: MouseEvent): void => {
      if (isSeeking) changeCurrentTimePosition(event.pageX);
    },
    [changeCurrentTimePosition, isSeeking]
  );

  const setTrackWidthState = useCallback((): void => {
    if (trackElement && trackWidth !== trackElement.offsetWidth)
      setTrackWidth(trackElement.offsetWidth);
  }, [trackElement, trackWidth]);

  const onHover = useCallback(
    (e: any): void => {
      let position: number = getMousePosition(e.pageX);
      setSeekHoverPosition(position);
      setTrackWidthState();
    },
    [getMousePosition, setTrackWidthState]
  );

  const offHover = useCallback((): void => {
    setSeekHoverPosition(0);
  }, []);

  const getPositionStyle = useCallback(
    (time: number): object => {
      let position: number = (time * 100) / videoDuration;
      return { transform: `scaleX(${position / 100})` };
    },
    [videoDuration]
  );

  const setSegmentationsPosition = useCallback(
    (startTime: number, endTime: number): object => {
      const half_second = (0.5 * 100) / videoDuration;
      const positionStart: number = (startTime * 100) / videoDuration;
      const positionEnd: number = (endTime * 100) / videoDuration;

      return {
        left: `${positionStart === positionEnd ? positionStart - half_second : positionStart}%`,
        right: `${100 - positionEnd}%`,
      };
    },
    [videoDuration]
  );

  const setSegmentationDetailsPositionAndColor = useCallback(
    (groups: GroupedSegmentations[], index: number): object => {
      const group = groups[index];

      const half_second = (0.5 * 100) / videoDuration;
      const startTime = group.segmentations[0].timestamp;
      const endTime = group.segmentations[group.segmentations.length - 1].timestamp;
      const positionStart: number = (startTime * 100) / videoDuration;
      const positionEnd: number = (endTime * 100) / videoDuration;

      const groupsOptions = videoAnalysis!.detectedGroupCodes
        .filter((o) => groups.findIndex((g) => g.groupCode === o.code) !== -1)
        .reverse();
      const groupIndex = groupsOptions.findIndex((o) => o.code === group.groupCode);

      return {
        left: `${positionStart === positionEnd ? positionStart - half_second : positionStart}%`,
        right: `${100 - positionEnd}%`,
        bottom: groupIndex * 10,
        backgroundColor: groupsOptions[groupIndex]?.colorCode,
      };
    },
    [videoAnalysis, videoDuration]
  );

  const getThumbHandlerPosition = useCallback((): object => {
    let position: number = trackWidth / (videoDuration / currentTime);
    return { transform: `translateX(${position}px)` };
  }, [currentTime, trackWidth, videoDuration]);

  const getSeekHoverPosition = useMemo((): object => {
    let position: number = (seekHoverPosition * 100) / trackWidth;
    return { transform: `scaleX(${position / 100})` };
  }, [seekHoverPosition, trackWidth]);

  const setSeeking = useCallback(
    (isSeeking: boolean, event: MouseEvent): void => {
      event.preventDefault();

      handleSeeking(event);
      setIsSeeking(isSeeking);

      setSeekHoverPosition((seekHoverPosition: number) => (isSeeking ? seekHoverPosition : 0));
    },
    [handleSeeking]
  );

  const mouseSeekingHandler = useCallback(
    (event: MouseEvent): void => {
      setSeeking(false, event);
    },
    [setSeeking]
  );

  const thumbIsActive = useMemo((): boolean => {
    return seekHoverPosition > 0 || isSeeking;
  }, [seekHoverPosition, isSeeking]);

  useEffect(() => {
    setTrackWidthState();
    const playerElement = player.current;
    playerElement?.addEventListener("resize", setTrackWidthState);
    playerElement?.addEventListener("mousemove", handleSeeking);
    playerElement?.addEventListener("mouseup", mouseSeekingHandler);
    return () => {
      playerElement?.removeEventListener("resize", setTrackWidthState);
      playerElement?.removeEventListener("mousemove", handleSeeking);
      playerElement?.removeEventListener("mouseup", mouseSeekingHandler);
    };
  }, [handleSeeking, mouseSeekingHandler, player, setTrackWidthState]);

  return (
    <div className="ui-video-seek-slider">
      <div
        className={thumbIsActive ? "track active" : "track"}
        ref={(ref) => setTrackElement(ref)}
        onMouseMove={onHover}
        onMouseLeave={offHover}
        onMouseDown={(e) => setSeeking(true, e as any)}
      >
        <div className="main">
          <div className="buffered" style={getPositionStyle(bufferedTime)} />

          <div className="seek-hover" style={getSeekHoverPosition} />

          <div className="connect" style={getPositionStyle(currentTime)} />

          {segmentationGroups &&
            segmentationGroups?.length &&
            segmentationGroups?.map((g) => (
              <div
                key={`${g[0].timestamp}-${g[g.length - 1].timestamp}`}
                className="segmentation"
                style={setSegmentationsPosition(g[0].timestamp, g[g.length - 1].timestamp)}
              />
            ))}
        </div>
      </div>
      {videoAnalysis?.showAllSegmentationBars && <div className="segmentations-background" />}
      <div className="segmentation-details-container">
        {segmentationGroupDetail &&
          segmentationGroupDetail.length > 0 &&
          segmentationGroupDetail.map((g: GroupedSegmentations, index: number) => (
            <div
              key={`${g.groupCode}-${index}`}
              className="segmentation-details"
              style={setSegmentationDetailsPositionAndColor(segmentationGroupDetail, index)}
            />
          ))}
      </div>
      <SliderTooltip
        isThumbActive={thumbIsActive}
        seekHoverPosition={seekHoverPosition}
        trackWidth={trackWidth}
        videoDuration={videoDuration}
      />
      <div className={thumbIsActive ? "thumb active" : "thumb"} style={getThumbHandlerPosition()}>
        <div className="handler" />
      </div>
    </div>
  );
});
